define(function(require) {
    const _ = require('underscore');
    const $ = require('jquery');
    const moment = require('moment');
    const FileSaver = require('fileSaver');
    const UserSettingUtil = require('utils/userSetting');

    const FileUtil = {
        /**
         * Download a file
         * @param  {Object} options Configuration object
         * @param  {String} options.url The full url of the file
         * @param  {String} [options.filename] Optional: Override filename for the downloaded file
         * @param  {String} [options.method='GET'] Optional: the method to use. Defaults to GET.
         * @param  {String} [options.contentType] Optional: the Content-Type to add to the headers.
         * @param  {String} [options.body] Optional: the body to include.
         * @return {jquery.Deferred} A jQuery Deferred object that resolves with the XHR response
         */
        downloadFile: function(options) {
            const { url, filename, method = 'GET', contentType, body } = options;

            if (!url) {
                return $.Deferred().reject(new Error('URL is required'));
            }

            const xhr = new XMLHttpRequest();
            const deferred = $.Deferred();

            xhr.onload = function() {
                if (xhr.status === 200) {
                    let finalFilename = filename;

                    // If no override filename provided, try to get from Content-Disposition
                    if (!finalFilename) {
                        try {
                            const disposition = xhr.getResponseHeader('Content-Disposition');
                            if (disposition) {
                                const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                                if (filenameMatch && filenameMatch[1]) {
                                    finalFilename = filenameMatch[1].replace(/['"]/g, '');
                                }
                            }
                        } catch (e) {
                            // Header might not be accessible
                        }
                    }

                    // Fallback to default if neither override nor Content-Disposition provided
                    finalFilename = finalFilename || 'download';

                    FileSaver.saveAs(xhr.response, finalFilename);
                    deferred.resolve(xhr);
                } else {
                    deferred.reject(xhr);
                }
            };

            xhr.onerror = function() {
                deferred.reject(xhr);
            };

            xhr.onabort = function() {
                deferred.reject(xhr);
            };

            xhr.open(method, url);
            xhr.withCredentials = true;
            xhr.responseType = 'blob';

            if (contentType) {
                xhr.setRequestHeader('Content-Type', contentType);
            }

            if (body) {
                xhr.send(body);
            } else {
                xhr.send();
            }

            return deferred;
        },

        // Formats a collection for CSV export
        formatCollectionForExport: function(collection, columnsToExport, includeTitle) {
            const self = this;
            let exportData = '';

            // Add values from model
            collection.each(function(model) {
                let modelRowStr = '';
                _.each(columnsToExport, function(column) {
                    let cellValue = '';
                    let rawValue = model.get(column.col);
                    // Look for '.' separator in column name, and split on that if found.
                    // This is currently only used in a Backgrid table, see
                    // pages/audience/FacebookExportBeacon.view.js
                    if (column.col.indexOf('.') !== -1) {
                        const names = column.col.split('.');
                        const field = model.get(names[0]);
                        rawValue = _.isFunction(field.get) ? field.get(names[1]) : field[names[1]];
                        // Support <field.field.field>
                        if (names.length == 3) {
                            rawValue = rawValue[names[2]];
                        }
                    }
                    if (!_.isUndefined(rawValue)) {
                        if (!_.isUndefined(column.exportFormatter)) {
                            // Use the explicitly set export formatter function
                            cellValue = column.exportFormatter(rawValue, model);
                        } else if (!_.isUndefined(column.formatter) && column.cell !== 'html') {
                            // Use the custom formatter for non-html cells
                            cellValue = column.formatter.fromRaw(rawValue, model) || '';
                        } else if (!_.isUndefined(column.render)) {
                            // Use the Datagrid render method
                            // The grid may not have the model if not on the current page, so pass it
                            cellValue = column.render(rawValue, 'export', null, null, model);
                        } else {
                            cellValue = rawValue;
                        }
                        cellValue = self.formatValueForCSV(cellValue);
                    }
                    modelRowStr = `${modelRowStr + cellValue},`;
                });
                exportData = `${exportData + modelRowStr.substring(0, modelRowStr.length - 1)}\n`;
            });

            if (includeTitle) {
                const columnNames = _.pluck(columnsToExport, 'displayName');
                return `${_.uniq(columnNames)}\n${exportData}`;
            } else {
                return exportData;
            }
        },

        buildCSVFile: function(csvData, fileName) {
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });

            // This is part of fileSave
            // eslint-disable-next-line no-undef
            FileSaver.saveAs(blob, `${fileName}.csv`);
        },

        // Rules for CSV formatting:
        // 1. If the value contains a comma, newline or double quote, then the String value should
        //    be returned enclosed in double quotes.
        // 2. Any double quote characters in the value should be escaped with another double quote.
        // 3. If the value does not contain a comma, newline or double quote, then the String value
        //    should be returned unchanged.
        formatValueForCSV: function(cellValue) {
            // escape commas to not mess up columns
            if (typeof cellValue == 'string' && cellValue.match(/[,\n"]/)) {
                cellValue = cellValue.replace(/"/g, '""');
                cellValue = `"${cellValue}"`;
            }
            // print nulls as blank
            if (cellValue === null) {
                cellValue = '';
            }
            // CSV really hates numeric strings with a trailing +, e.g '65+'
            if (typeof cellValue == 'string' && cellValue.match(/^[\d]+\+$/)) {
                cellValue = `${cellValue.substring(0, cellValue.length - 1)} plus`;
            }
            return cellValue;
        },

        /**
         * Produces the filename used for the exported file.
         * @param  {Object} options, see below for attributes and defaults
         * @return {String} the filename
         */
        generateFilename(options = {}) {
            const defaultOptions = {
                reportName: undefined, // name of page/report
                clientId: undefined,
                clientName: undefined,
                entityId: undefined, // id of audience/behavior etc.
                entityName: undefined, // name of audience/behavior etc.
                dataDate: undefined // reference date for data in YYYYMMDD format
            };
            const appliedOptions = Object.assign({}, defaultOptions, options);

            // For each part of the filename, remove any funky characters we
            // don't want in a filename, and convert whitespace to underscores
            function prepComponent(component) {
                return component
                    .toString()
                    .trim()
                    .replace(/[/<>:"\\|?*]/g, '')
                    .replace(/\s+/g, '_');
            }

            // Truncates any long entity names to be only the 1st and last 20 characters separated by '-'
            // For example:  '12345678901234567890123456789012345678901234567890'
            // should become '12345678901234567890-12345678901234567890'
            function formatEntityName(name) {
                const halfMaxEntityLength = 20; // half because we use beginning & end
                let formattedName = prepComponent(name);
                if (formattedName.length > halfMaxEntityLength * 2) {
                    formattedName = `${formattedName.substring(0, halfMaxEntityLength)}-${formattedName.substring(
                        formattedName.length - halfMaxEntityLength
                    )}`;
                }
                return formattedName;
            }

            const components = [];
            if (!_.isUndefined(appliedOptions.reportName)) {
                components.push(appliedOptions.reportName);
            }
            if (!_.isUndefined(appliedOptions.clientId)) {
                components.push(appliedOptions.clientId);
            }
            if (!_.isUndefined(appliedOptions.clientName)) {
                components.push(appliedOptions.clientName);
            }
            if (!_.isUndefined(appliedOptions.entityId)) {
                components.push(appliedOptions.entityId);
            }
            if (!_.isUndefined(appliedOptions.entityName)) {
                components.push(formatEntityName(appliedOptions.entityName));
            }
            if (!_.isUndefined(appliedOptions.dataDate)) {
                // 'ref' to indicate that this is a refresh date,
                // as in, when the data was last refreshed
                components.push('ref');
                components.push(appliedOptions.dataDate);
            } else {
                // If no refresh date, add the download date
                components.push(moment().format('YYYYMMDD'));
            }
            return components.map(c => prepComponent(c)).join('_');
        },

        /**
         * Determines format / file extension to use for spreadsheet downloads.
         * Looks to user setting first, and defaults to csv.
         * @return {String} the format / file extension
         */
        getSpreadsheetFormat() {
            return UserSettingUtil.getUserSetting('spreadsheetFileFormat') || 'csv';
        }
    };
    return FileUtil;
});
